import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import {
  AuthContainer,
  Logo,
  AuthBox,
  FormContainer,
  LinkContainer,
} from '../../styles/Auth.styles';

const TwoFactorVerification = () => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  
  const { verify2FA, resend2FACode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get data from location state
  const userId = location.state?.userId;
  const email = location.state?.email;
  const emailSent = location.state?.emailSent;
  const emailError = location.state?.emailError;
  const adminFallbackCode = location.state?.adminFallbackCode;
  
  // Redirect to login if no userId is provided
  if (!userId) {
    navigate('/login');
    return null;
  }
  
  // Mask email for display
  const maskEmail = (email) => {
    if (!email) return '';
    const [username, domain] = email.split('@');
    const maskedUsername = username.charAt(0) + 
      '*'.repeat(Math.max(1, username.length - 2)) + 
      (username.length > 1 ? username.charAt(username.length - 1) : '');
    return `${maskedUsername}@${domain}`;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('Please enter the verification code');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const user = await verify2FA(userId, code);
      if (user.role === 'admin' || user.role === 'accountant') {
        navigate('/reporting');
      } else {
        navigate('/timecard');
      }
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };
  
  const handleResendCode = async () => {
    setResending(true);
    setError('');
    
    try {
      await resend2FACode(userId);
      // Show success message
      setError('');
      alert('A new verification code has been sent to your email');
    } catch (error) {
      setError(error.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthContainer>
      <Logo
        src="/images/eci_logo_color.svg"
        alt="ECI Logo"
      />
      <AuthBox>
        <Typography component="h1" variant="h5" align="center" gutterBottom>
          Two-Factor Authentication
        </Typography>
        {emailSent !== false ? (
          <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 3 }}>
            A verification code has been sent to {maskEmail(email)}
          </Typography>
        ) : (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {emailError || 'Email service is temporarily unavailable.'}
          </Alert>
        )}
        
        {adminFallbackCode && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Admin Fallback Code:</strong> {adminFallbackCode}
            </Typography>
            <Typography variant="caption">
              This code is only shown to administrators when email service is unavailable.
            </Typography>
          </Alert>
        )}
        
        <FormContainer component="form" onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <TextField
            margin="normal"
            required
            fullWidth
            label="Verification Code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
            inputProps={{ 
              maxLength: 6,
              inputMode: 'numeric',
              pattern: '[0-9]*'
            }}
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Verify'}
          </Button>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <LinkContainer>
              <Link to="/login">
                Back to Login
              </Link>
            </LinkContainer>
            
            <LinkContainer>
              <Link 
                to="#" 
                onClick={(e) => {
                  e.preventDefault();
                  handleResendCode();
                }}
                style={{ pointerEvents: resending ? 'none' : 'auto' }}
              >
                {resending ? <CircularProgress size={20} /> : 'Resend Code'}
              </Link>
            </LinkContainer>
          </Box>
        </FormContainer>
      </AuthBox>
    </AuthContainer>
  );
};

export default TwoFactorVerification;
